import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Chart as ChartJS,
  registerables,
  TooltipItem,
} from "chart.js";
import {
  AlertCircle,
  BarChart3,
  Calendar,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Clock,
  Eye,
  Filter,
  Layers,
  Mail,
  PieChart,
  RefreshCw,
  RotateCcw,
  Search,
  ShoppingCart,
  Trash2,
  TrendingUp,
  User,
  X,
} from "lucide-react";
import adminApi from "../adminApi";
import { useLanguage } from "../context/LanguageContext";
import { useDialogs } from "../context/DialogContext";
import "./Carts.css";

ChartJS.register(...registerables);

export type CartStatus = "all" | "ordered" | "inquiry" | "abandoned";
export type DatePreset = "all" | "today" | "thisMonth" | "lastMonth" | "thisYear" | "day" | "month" | "range";
export type ViewMode = "all" | "dashboard" | "table";

interface CustomerInfo {
  name?: string;
  email?: string;
  phone?: string;
  company?: string;
  streetAndHouse?: string;
  zipCode?: string;
  city?: string;
  land?: string;
}

interface CartDoor {
  id: number;
  doorName: string;
  lockAmount: number;
  lockType: string;
  lockOutside: number;
  lockInside: number;
}

interface CartKey {
  id: number;
  keyName: string;
  amount: number;
}

interface CartPermission {
  idDoor: number;
  idKey: number;
  allow?: boolean;
}

interface CartItem {
  _id: string;
  orderid: string | null;
  status: "ordered" | "inquiry" | "abandoned";
  supllier: string;
  model: string;
  doorsCount: number;
  totalDoorsAmount: number;
  keysCount: number;
  totalKeysAmount: number;
  netto: number;
  brutto: number;
  comment?: string;
  customer?: CustomerInfo | null;
  createdAt: string | null;
  dateKey?: string | null;
  monthKey?: string | null;
  orderedAt?: string | null;
  inquiryAt?: string | null;
}

interface CartDetail extends CartItem {
  doors?: CartDoor[];
  keys?: CartKey[];
  opensDoor?: CartPermission[];
}

interface SupplierStat {
  name: string;
  count: number;
  orderedCount: number;
  inquiryCount: number;
  abandonedCount: number;
  netto: number;
  orderedNetto: number;
  avgNetto: number;
}

interface ModelStat {
  name: string;
  supplier: string;
  count: number;
  orderedCount: number;
  inquiryCount: number;
  abandonedCount: number;
  netto: number;
  orderedNetto: number;
  avgNetto: number;
}

interface TimeStat {
  key: string;
  label: string;
  total: number;
  ordered: number;
  inquiry: number;
  abandoned: number;
  netto: number;
  orderedNetto: number;
}

interface CartStats {
  total: number;
  ordered: number;
  inquiry: number;
  abandoned: number;
  totalNetto: number;
  orderedNetto: number;
  inquiryNetto: number;
  abandonedNetto: number;
  totalBrutto: number;
  orderedBrutto: number;
  inquiryBrutto: number;
  abandonedBrutto: number;
  orderRate: number;
  inquiryRate: number;
  conversionRate: number;
  avgNetto: number;
  avgOrderNetto: number;
  bySupplier: SupplierStat[];
  byModel: ModelStat[];
  byTime: TimeStat[];
  availableSuppliers: string[];
  availableModels: string[];
  availableMonths: string[];
  availableDates: string[];
}

const SUPPLIER_COLORS: Record<string, string> = {
  CES: "#005CA9",
  EVVA: "#059669",
  ABUS: "#D97706",
  BKS: "#E11D48",
  IKON: "#7C3AED",
};

const getSupplierColor = (name: string): string => {
  const upper = (name || "").toUpperCase();
  if (SUPPLIER_COLORS[upper]) return SUPPLIER_COLORS[upper];
  const palette = ["#2563EB", "#059669", "#D97706", "#E11D48", "#7C3AED", "#0891B2", "#4F46E5"];
  let hash = 0;
  for (let i = 0; i < upper.length; i++) hash += upper.charCodeAt(i);
  return palette[Math.abs(hash) % palette.length];
};

const formatCurrency = (val?: number | null): string => {
  if (val == null || isNaN(val)) return "0,00 €";
  return val.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
};

const Carts: React.FC = () => {
  const { t, tf } = useLanguage();
  const { notify, confirm } = useDialogs();

  // Dane z API
  const [carts, setCarts] = useState<CartItem[]>([]);
  const [stats, setStats] = useState<CartStats>({
    total: 0,
    ordered: 0,
    inquiry: 0,
    abandoned: 0,
    totalNetto: 0,
    orderedNetto: 0,
    inquiryNetto: 0,
    abandonedNetto: 0,
    totalBrutto: 0,
    orderedBrutto: 0,
    inquiryBrutto: 0,
    abandonedBrutto: 0,
    orderRate: 0,
    inquiryRate: 0,
    conversionRate: 0,
    avgNetto: 0,
    avgOrderNetto: 0,
    bySupplier: [],
    byModel: [],
    byTime: [],
    availableSuppliers: [],
    availableModels: [],
    availableMonths: [],
    availableDates: [],
  });

  const [loading, setLoading] = useState(true);

  // Filtry
  const [selectedStatus, setSelectedStatus] = useState<CartStatus>("all");
  const [selectedSupplier, setSelectedSupplier] = useState<string>("all");
  const [selectedModel, setSelectedModel] = useState<string>("all");
  const [datePreset, setDatePreset] = useState<DatePreset>("all");
  const [customDay, setCustomDay] = useState<string>("");
  const [customMonth, setCustomMonth] = useState<string>("");
  const [customRangeFrom, setCustomRangeFrom] = useState<string>("");
  const [customRangeTo, setCustomRangeTo] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState("");

  // Paginacja i sortowanie tabeli
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalFiltered, setTotalFiltered] = useState(0);
  const [sortKey, setSortKey] = useState<string>("date");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  // Tryb widoku (Dashboard / Tabela / Oba)
  const [viewMode, setViewMode] = useState<ViewMode>("all");

  // Modal ze szczegółami
  const [selectedCartId, setSelectedCartId] = useState<string | null>(null);
  const [cartDetail, setCartDetail] = useState<CartDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Referencje do wykresów Chart.js
  const canvasRefPie = useRef<HTMLCanvasElement | null>(null);
  const canvasRefSys = useRef<HTMLCanvasElement | null>(null);
  const canvasRefTime = useRef<HTMLCanvasElement | null>(null);
  const canvasRefVal = useRef<HTMLCanvasElement | null>(null);

  const chartPieRef = useRef<ChartJS | null>(null);
  const chartSysRef = useRef<ChartJS | null>(null);
  const chartTimeRef = useRef<ChartJS | null>(null);
  const chartValRef = useRef<ChartJS | null>(null);

  // Pobieranie danych koszyków i statystyk
  const fetchCarts = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();

      if (selectedStatus !== "all") params.append("status", selectedStatus);
      if (selectedSupplier !== "all") params.append("supplier", selectedSupplier);
      if (selectedModel !== "all") params.append("model", selectedModel);

      // Logika filtrów czasowych
      if (datePreset === "today") {
        const todayStr = new Date().toISOString().slice(0, 10);
        params.append("date", todayStr);
      } else if (datePreset === "thisMonth") {
        const thisMonthStr = new Date().toISOString().slice(0, 7);
        params.append("month", thisMonthStr);
      } else if (datePreset === "lastMonth") {
        const d = new Date();
        d.setMonth(d.getMonth() - 1);
        const lastMonthStr = d.toISOString().slice(0, 7);
        params.append("month", lastMonthStr);
      } else if (datePreset === "thisYear") {
        const yearStr = String(new Date().getFullYear());
        params.append("dateFrom", `${yearStr}-01-01`);
        params.append("dateTo", `${yearStr}-12-31`);
      } else if (datePreset === "day" && customDay) {
        params.append("date", customDay);
      } else if (datePreset === "month" && customMonth) {
        params.append("month", customMonth);
      } else if (datePreset === "range") {
        if (customRangeFrom) params.append("dateFrom", customRangeFrom);
        if (customRangeTo) params.append("dateTo", customRangeTo);
      }

      if (searchQuery.trim()) params.append("search", searchQuery.trim());
      params.append("page", String(page));
      params.append("limit", "25");

      const response = await adminApi.get(`/carts?${params.toString()}`);
      setCarts(response.data.carts || []);
      if (response.data.stats) {
        setStats(response.data.stats);
      }
      if (response.data.pagination) {
        setTotalPages(response.data.pagination.totalPages || 1);
        setTotalFiltered(response.data.pagination.total || 0);
      }
    } catch (err) {
      console.error("Błąd podczas pobierania koszyków:", err);
      notify(t("Failed to load carts"), "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCarts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStatus, selectedSupplier, selectedModel, datePreset, customDay, customMonth, customRangeFrom, customRangeTo, page]);

  // Kliknięcie w kafelek KPI (włącza lub wyłącza filtr statusu)
  const handleKpiClick = (statusKey: CartStatus) => {
    if (selectedStatus === statusKey) {
      setSelectedStatus("all");
    } else {
      setSelectedStatus(statusKey);
    }
    setPage(1);
  };

  // Resetowanie wszystkich filtrów
  const handleResetFilters = () => {
    setSelectedStatus("all");
    setSelectedSupplier("all");
    setSelectedModel("all");
    setDatePreset("all");
    setCustomDay("");
    setCustomMonth("");
    setCustomRangeFrom("");
    setCustomRangeTo("");
    setSearchQuery("");
    setPage(1);
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    fetchCarts();
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setPage(1);
  };

  // Sortowanie tabeli
  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  const sortedCarts = useMemo(() => {
    const list = [...carts];
    list.sort((a, b) => {
      let valA: any = "";
      let valB: any = "";

      switch (sortKey) {
        case "id":
          valA = a.orderid || a._id;
          valB = b.orderid || b._id;
          break;
        case "supplier":
          valA = a.supllier || "";
          valB = b.supllier || "";
          break;
        case "model":
          valA = a.model || "";
          valB = b.model || "";
          break;
        case "doors":
          valA = a.doorsCount || 0;
          valB = b.doorsCount || 0;
          break;
        case "keys":
          valA = a.totalKeysAmount || 0;
          valB = b.totalKeysAmount || 0;
          break;
        case "netto":
          valA = a.netto || 0;
          valB = b.netto || 0;
          break;
        case "status":
          valA = a.status || "";
          valB = b.status || "";
          break;
        case "date":
        default:
          valA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          valB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          break;
      }

      if (typeof valA === "number" && typeof valB === "number") {
        return sortDir === "asc" ? valA - valB : valB - valA;
      }
      return sortDir === "asc"
        ? String(valA).localeCompare(String(valB))
        : String(valB).localeCompare(String(valA));
    });
    return list;
  }, [carts, sortKey, sortDir]);

  // Szczegóły koszyka w modalu
  const openCartDetail = async (id: string) => {
    setSelectedCartId(id);
    setLoadingDetail(true);
    try {
      const response = await adminApi.get(`/carts/${id}`);
      setCartDetail(response.data);
    } catch (err) {
      console.error("Błąd pobierania szczegółów koszyka:", err);
      notify(t("Failed to load carts"), "error");
      setSelectedCartId(null);
    } finally {
      setLoadingDetail(false);
    }
  };

  const closeCartDetail = () => {
    setSelectedCartId(null);
    setCartDetail(null);
  };

  const handleStatusChange = async (id: string, newStatus: "ordered" | "inquiry" | "abandoned") => {
    try {
      await adminApi.put(`/carts/${id}/status`, { status: newStatus });
      notify(t("Status updated successfully!"), "success");
      if (cartDetail && cartDetail._id === id) {
        setCartDetail({ ...cartDetail, status: newStatus });
      }
      fetchCarts();
    } catch (err) {
      console.error("Błąd podczas zmiany statusu:", err);
      notify(t("Failed to update cart status"), "error");
    }
  };

  const handleDeleteCart = async (id: string) => {
    const ok = await confirm(t("Are you sure you want to delete this cart?"));
    if (!ok) return;

    try {
      await adminApi.delete(`/carts/${id}`);
      notify(t("Cart deleted successfully!"), "success");
      if (selectedCartId === id) {
        closeCartDetail();
      }
      fetchCarts();
    } catch (err) {
      console.error("Błąd podczas usuwania koszyka:", err);
      notify(t("Failed to delete cart"), "error");
    }
  };

  const formatDate = (isoString?: string | null) => {
    if (!isoString) return "—";
    try {
      const d = new Date(isoString);
      return d.toLocaleDateString("de-DE", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "—";
    }
  };

  const renderStatusBadge = (status: "ordered" | "inquiry" | "abandoned") => {
    switch (status) {
      case "ordered":
        return (
          <span className="cart-status-badge cart-status-badge--ordered">
            <CheckCircle2 size={13} />
            {t("Ordered")}
          </span>
        );
      case "inquiry":
        return (
          <span className="cart-status-badge cart-status-badge--inquiry">
            <Mail size={13} />
            {t("Inquiries")}
          </span>
        );
      case "abandoned":
      default:
        return (
          <span className="cart-status-badge cart-status-badge--abandoned">
            <AlertCircle size={13} />
            {t("Abandoned")}
          </span>
        );
    }
  };

  // Inicjalizacja i aktualizacja wykresów Chart.js
  useEffect(() => {
    if (viewMode === "table") return;

    // Wspólna konfiguracja tooltipów i czcionek w stylu strony
    const tooltipOptions = {
      backgroundColor: "#1D1F58",
      borderColor: "rgba(255, 255, 255, 0.15)",
      borderWidth: 1,
      borderRadius: 8,
      padding: 10,
      titleFont: { size: 12, weight: "bold" as const },
      bodyFont: { size: 12 },
      bodyColor: "#FFFFFF",
      titleColor: "#FFFFFF",
    };

    // 1. Doughnut Chart: Udział per dostawca
    if (canvasRefPie.current) {
      if (chartPieRef.current) chartPieRef.current.destroy();

      const pieSuppliers = stats.bySupplier || [];
      const labels = pieSuppliers.map((s) => s.name);
      const dataCounts = pieSuppliers.map((s) => s.count);
      const bgColors = pieSuppliers.map((s) => getSupplierColor(s.name));

      chartPieRef.current = new ChartJS(canvasRefPie.current, {
        type: "doughnut",
        data: {
          labels,
          datasets: [
            {
              data: dataCounts,
              backgroundColor: bgColors,
              borderWidth: 2,
              borderColor: "#FFFFFF",
              hoverOffset: 6,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutout: "60%",
          plugins: {
            legend: {
              position: "right",
              labels: {
                boxWidth: 12,
                padding: 10,
                font: { size: 11, family: "inherit" },
                color: "#5F6B7A",
              },
            },
            tooltip: {
              ...tooltipOptions,
              callbacks: {
                label: (ctx: TooltipItem<"doughnut">) => {
                  const val = Number(ctx.raw) || 0;
                  const total = stats.total || 1;
                  const pct = ((val / total) * 100).toFixed(1);
                  return ` ${ctx.label}: ${val} (${pct}%)`;
                },
              },
            },
          },
        },
      });
    }

    // 2. Bar Chart: Top 10 systemów
    if (canvasRefSys.current) {
      if (chartSysRef.current) chartSysRef.current.destroy();

      const topModels = (stats.byModel || []).slice(0, 10);
      const labels = topModels.map((m) => m.name);
      const dataCounts = topModels.map((m) => m.count);
      const bgColors = topModels.map((m) => getSupplierColor(m.supplier));

      chartSysRef.current = new ChartJS(canvasRefSys.current, {
        type: "bar",
        data: {
          labels,
          datasets: [
            {
              label: t("Count"),
              data: dataCounts,
              backgroundColor: bgColors,
              borderRadius: 6,
              maxBarThickness: 34,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              ...tooltipOptions,
              callbacks: {
                afterLabel: (ctx: TooltipItem<"bar">) => {
                  const item = topModels[ctx.dataIndex];
                  if (!item) return "";
                  return `${t("Supplier")}: ${item.supplier}\n${t("Sold")}: ${item.orderedCount} | Netto: ${formatCurrency(item.netto)}`;
                },
              },
            },
          },
          scales: {
            y: {
              beginAtZero: true,
              grid: { color: "rgba(221, 227, 234, 0.7)" },
              ticks: { font: { size: 11 }, color: "#8A94A6" },
            },
            x: {
              grid: { display: false },
              ticks: {
                font: { size: 10 },
                color: "#5F6B7A",
                maxRotation: 40,
                autoSkip: false,
              },
            },
          },
        },
      });
    }

    // 3. Stacked Bar Chart: Konfiguracje w czasie
    if (canvasRefTime.current) {
      if (chartTimeRef.current) chartTimeRef.current.destroy();

      const timeItems = (stats.byTime || []).slice(-18);
      const labels = timeItems.map((t) => t.label);
      const dataKorb = timeItems.map((t) => t.abandoned);
      const dataOrdered = timeItems.map((t) => t.ordered);
      const dataInquiry = timeItems.map((t) => t.inquiry);

      chartTimeRef.current = new ChartJS(canvasRefTime.current, {
        type: "bar",
        data: {
          labels,
          datasets: [
            {
              label: t("Abandoned"),
              data: dataKorb,
              backgroundColor: "rgba(0, 92, 169, 0.75)",
              stack: "timeline",
            },
            {
              label: t("Ordered"),
              data: dataOrdered,
              backgroundColor: "rgba(30, 142, 62, 0.8)",
              stack: "timeline",
            },
            {
              label: t("Inquiries"),
              data: dataInquiry,
              backgroundColor: "rgba(217, 119, 6, 0.8)",
              borderRadius: { topLeft: 4, topRight: 4 },
              stack: "timeline",
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: "top",
              labels: {
                boxWidth: 10,
                padding: 12,
                font: { size: 11 },
                color: "#5F6B7A",
              },
            },
            tooltip: tooltipOptions,
          },
          scales: {
            x: {
              stacked: true,
              grid: { display: false },
              ticks: { font: { size: 10 }, color: "#8A94A6", maxRotation: 45 },
            },
            y: {
              stacked: true,
              beginAtZero: true,
              grid: { color: "rgba(221, 227, 234, 0.7)" },
              ticks: { font: { size: 11 }, color: "#8A94A6" },
            },
          },
        },
      });
    }

    // 4. Horizontal Bar: Wartość netto per dostawca (€)
    if (canvasRefVal.current) {
      if (chartValRef.current) chartValRef.current.destroy();

      const suppVal = [...(stats.bySupplier || [])].sort((a, b) => b.netto - a.netto);
      const labels = suppVal.map((s) => s.name);
      const dataNetto = suppVal.map((s) => s.netto);
      const bgColors = suppVal.map((s) => getSupplierColor(s.name));

      chartValRef.current = new ChartJS(canvasRefVal.current, {
        type: "bar",
        data: {
          labels,
          datasets: [
            {
              label: t("Total Net"),
              data: dataNetto,
              backgroundColor: bgColors,
              borderRadius: 6,
              maxBarThickness: 36,
            },
          ],
        },
        options: {
          indexAxis: "y",
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              ...tooltipOptions,
              callbacks: {
                label: (ctx: TooltipItem<"bar">) => {
                  const val = Number(ctx.raw) || 0;
                  return ` Netto: ${formatCurrency(val)}`;
                },
              },
            },
          },
          scales: {
            x: {
              beginAtZero: true,
              grid: { color: "rgba(221, 227, 234, 0.7)" },
              ticks: {
                font: { size: 10 },
                color: "#8A94A6",
                callback: (v) => `${(Number(v) / 1000).toFixed(0)}k €`,
              },
            },
            y: {
              grid: { display: false },
              ticks: { font: { size: 11, weight: "bold" }, color: "#1F2933" },
            },
          },
        },
      });
    }

    return () => {
      if (chartPieRef.current) chartPieRef.current.destroy();
      if (chartSysRef.current) chartSysRef.current.destroy();
      if (chartTimeRef.current) chartTimeRef.current.destroy();
      if (chartValRef.current) chartValRef.current.destroy();
    };
  }, [stats, viewMode, t]);

  // Maksymalna liczba konfiguracji w tabeli Lieferanten (dla paska %)
  const maxSupplierCount = useMemo(() => {
    if (!stats.bySupplier || stats.bySupplier.length === 0) return 1;
    return Math.max(...stats.bySupplier.map((s) => s.count), 1);
  }, [stats.bySupplier]);

  return (
    <div className="carts-container">
      {/* Nagłówek widoku i metadane */}
      <div className="carts-header-top">
        <div>
          <div className="admin-page__header" style={{ marginBottom: 4, paddingBottom: 0, border: "none" }}>
            <h2>{t("Carts")} — {t("Dashboard")}</h2>
          </div>
          <p style={{ margin: 0, color: "var(--adm-text-muted)", fontSize: 13 }}>
            {t("Konfigurator")} &middot; MongoDB / NeDB &middot; {t("Statystyki koszyków i zamówień")}
          </p>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <div className="carts-header-badge">
            <span>{stats.total} {t("Entries")}</span>
            &middot;
            <span>{stats.availableSuppliers.length} {t("Supplier")}</span>
            &middot;
            <span style={{ color: "var(--adm-primary)", fontWeight: 700 }}>
              {formatCurrency(stats.totalNetto)} {t("Total Net")}
            </span>
          </div>

          {/* Przełącznik widoków */}
          <div className="carts-view-toggle">
            <button
              type="button"
              className={`carts-view-btn ${viewMode === "all" ? "carts-view-btn--active" : ""}`}
              onClick={() => setViewMode("all")}
              title={t("Dashboard & Table")}
            >
              <Layers size={14} />
              {t("Dashboard & Table")}
            </button>
            <button
              type="button"
              className={`carts-view-btn ${viewMode === "dashboard" ? "carts-view-btn--active" : ""}`}
              onClick={() => setViewMode("dashboard")}
              title={t("Dashboard Only")}
            >
              <PieChart size={14} />
              {t("Dashboard Only")}
            </button>
            <button
              type="button"
              className={`carts-view-btn ${viewMode === "table" ? "carts-view-btn--active" : ""}`}
              onClick={() => setViewMode("table")}
              title={t("Table Only")}
            >
              <BarChart3 size={14} />
              {t("Table Only")}
            </button>
          </div>

          <button
            type="button"
            className="admin-btn admin-btn--ghost"
            onClick={() => fetchCarts()}
            disabled={loading}
            title="Odśwież"
          >
            <RefreshCw size={15} className={loading ? "spin" : ""} />
          </button>
        </div>
      </div>

      {/* Pasek podpowiedzi o aktywnym filtrze */}
      <div className="carts-hint-banner">
        <div
          className="carts-hint-dot"
          style={{
            backgroundColor:
              selectedStatus === "ordered"
                ? "#1E8E3E"
                : selectedStatus === "inquiry"
                ? "#D97706"
                : selectedStatus === "abandoned"
                ? "#005CA9"
                : "#6366F1",
          }}
        />
        <span>
          {selectedStatus === "all"
            ? t("All order types — click card to filter")
            : tf("Filtered: {status} — click again for all", {
                status:
                  selectedStatus === "ordered"
                    ? t("Ordered")
                    : selectedStatus === "inquiry"
                    ? t("Inquiries")
                    : t("Abandoned"),
              })}
        </span>
      </div>

      {/* Interaktywne kafelki KPI (z wartościami netto) */}
      <div className="carts-kpi-row">
        {/* Gesamt / Alle */}
        <div
          className={`cart-kpi-card ${selectedStatus === "all" ? "cart-kpi-card--active" : ""}`}
          onClick={() => handleKpiClick("all")}
          title={t("All")}
        >
          <div className="cart-kpi-bar" style={{ background: "linear-gradient(90deg, #1D1F58, #005CA9)" }} />
          <div className="cart-kpi-head">
            <span className="cart-kpi-label">{t("All")}</span>
            <div className="cart-kpi-icon" style={{ background: "#E8F1F9", color: "#005CA9" }}>
              <ShoppingCart size={18} />
            </div>
          </div>
          <div className="cart-kpi-val">{stats.total}</div>
          <div className="cart-kpi-sub">
            {formatCurrency(stats.totalNetto)} netto
          </div>
          <div className="cart-kpi-extra">
            {stats.conversionRate}% {t("Conversion Rate")} &middot; {formatCurrency(stats.totalBrutto)} brutto
          </div>
        </div>

        {/* Warenkörbe / Abgebrochen */}
        <div
          className={`cart-kpi-card ${selectedStatus === "abandoned" ? "cart-kpi-card--active" : ""}`}
          onClick={() => handleKpiClick("abandoned")}
          title={t("Abandoned")}
        >
          <div className="cart-kpi-bar" style={{ background: "linear-gradient(90deg, #3B82F6, #60A5FA)" }} />
          <div className="cart-kpi-head">
            <span className="cart-kpi-label">{t("Abandoned")}</span>
            <div className="cart-kpi-icon" style={{ background: "#EBF3FB", color: "#005CA9" }}>
              <AlertCircle size={18} />
            </div>
          </div>
          <div className="cart-kpi-val">{stats.abandoned}</div>
          <div className="cart-kpi-sub">
            {formatCurrency(stats.abandonedNetto)} netto
          </div>
          <div className="cart-kpi-extra">
            {stats.total > 0 ? ((stats.abandoned / stats.total) * 100).toFixed(1) : 0}% {t("Warenkörbe gesamt")}
          </div>
        </div>

        {/* Bestellungen */}
        <div
          className={`cart-kpi-card ${selectedStatus === "ordered" ? "cart-kpi-card--active" : ""}`}
          onClick={() => handleKpiClick("ordered")}
          title={t("Ordered")}
        >
          <div className="cart-kpi-bar" style={{ background: "linear-gradient(90deg, #059669, #34D399)" }} />
          <div className="cart-kpi-head">
            <span className="cart-kpi-label">{t("Ordered")}</span>
            <div className="cart-kpi-icon" style={{ background: "#E6F4EA", color: "#1E8E3E" }}>
              <CheckCircle2 size={18} />
            </div>
          </div>
          <div className="cart-kpi-val">{stats.ordered}</div>
          <div className="cart-kpi-sub" style={{ color: "#1E8E3E" }}>
            {formatCurrency(stats.orderedNetto)} netto
          </div>
          <div className="cart-kpi-extra">
            {stats.orderRate}% {t("Bestellquote")} &middot; Ø {formatCurrency(stats.avgOrderNetto)}
          </div>
        </div>

        {/* Anfragen */}
        <div
          className={`cart-kpi-card ${selectedStatus === "inquiry" ? "cart-kpi-card--active" : ""}`}
          onClick={() => handleKpiClick("inquiry")}
          title={t("Inquiries")}
        >
          <div className="cart-kpi-bar" style={{ background: "linear-gradient(90deg, #D97706, #FBBF24)" }} />
          <div className="cart-kpi-head">
            <span className="cart-kpi-label">{t("Inquiries")}</span>
            <div className="cart-kpi-icon" style={{ background: "#FEF7E0", color: "#D97706" }}>
              <Mail size={18} />
            </div>
          </div>
          <div className="cart-kpi-val">{stats.inquiry}</div>
          <div className="cart-kpi-sub" style={{ color: "#D97706" }}>
            {formatCurrency(stats.inquiryNetto)} netto
          </div>
          <div className="cart-kpi-extra">
            {stats.inquiryRate}% {t("Inquiries")} &middot; {formatCurrency(stats.inquiryBrutto)} brutto
          </div>
        </div>
      </div>

      {/* Pasek filtrów i opcji wyszukiwania */}
      <div className="carts-filter-panel">
        <div className="carts-filter-row">
          {/* Wybór producenta */}
          <div className="carts-filter-item">
            <span className="carts-filter-label">{t("Filter by Supplier")}:</span>
            <select
              className="carts-select"
              value={selectedSupplier}
              onChange={(e) => {
                setSelectedSupplier(e.target.value);
                setPage(1);
              }}
            >
              <option value="all">{t("All Suppliers")}</option>
              {stats.availableSuppliers.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>

          {/* Wybór systemu / produktu */}
          <div className="carts-filter-item">
            <span className="carts-filter-label">{t("Filter by System")}:</span>
            <select
              className="carts-select"
              value={selectedModel}
              onChange={(e) => {
                setSelectedModel(e.target.value);
                setPage(1);
              }}
            >
              <option value="all">{t("All Systems")}</option>
              {stats.availableModels.map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </div>

          {/* Szukajka tekstowa */}
          <form className="carts-search-wrap" onSubmit={handleSearchSubmit}>
            <Search size={16} className="carts-search-icon" />
            <input
              type="text"
              className="carts-search-input"
              placeholder={t("Search by Order ID, system, customer...")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button type="button" className="carts-search-clear" onClick={handleClearSearch}>
                <X size={14} />
              </button>
            )}
          </form>

          {/* Przycisk resetowania filtrów */}
          <button
            type="button"
            className="admin-btn admin-btn--ghost admin-btn--sm"
            onClick={handleResetFilters}
            title={t("Reset Filters")}
            style={{ gap: 5 }}
          >
            <RotateCcw size={13} />
            {t("Reset Filters")}
          </button>
        </div>

        {/* Drugi wiersz: filtry daty, dnia i miesiąca */}
        <div className="carts-filter-row" style={{ borderTop: "1px solid var(--adm-border)", paddingTop: 10 }}>
          <div className="carts-filter-group">
            <span className="carts-filter-label">{t("Time Period")}:</span>

            <div className="carts-preset-pills">
              <button
                type="button"
                className={`carts-preset-pill ${datePreset === "all" ? "carts-preset-pill--active" : ""}`}
                onClick={() => { setDatePreset("all"); setPage(1); }}
              >
                {t("All Time")}
              </button>
              <button
                type="button"
                className={`carts-preset-pill ${datePreset === "today" ? "carts-preset-pill--active" : ""}`}
                onClick={() => { setDatePreset("today"); setPage(1); }}
              >
                {t("Today")}
              </button>
              <button
                type="button"
                className={`carts-preset-pill ${datePreset === "thisMonth" ? "carts-preset-pill--active" : ""}`}
                onClick={() => { setDatePreset("thisMonth"); setPage(1); }}
              >
                {t("This Month")}
              </button>
              <button
                type="button"
                className={`carts-preset-pill ${datePreset === "lastMonth" ? "carts-preset-pill--active" : ""}`}
                onClick={() => { setDatePreset("lastMonth"); setPage(1); }}
              >
                {t("Last Month")}
              </button>
              <button
                type="button"
                className={`carts-preset-pill ${datePreset === "thisYear" ? "carts-preset-pill--active" : ""}`}
                onClick={() => { setDatePreset("thisYear"); setPage(1); }}
              >
                {t("This Year")}
              </button>
              <button
                type="button"
                className={`carts-preset-pill ${datePreset === "day" ? "carts-preset-pill--active" : ""}`}
                onClick={() => {
                  setDatePreset("day");
                  if (!customDay && stats.availableDates[0]) setCustomDay(stats.availableDates[0]);
                  setPage(1);
                }}
              >
                {t("Specific Day")}
              </button>
              <button
                type="button"
                className={`carts-preset-pill ${datePreset === "month" ? "carts-preset-pill--active" : ""}`}
                onClick={() => {
                  setDatePreset("month");
                  if (!customMonth && stats.availableMonths[0]) setCustomMonth(stats.availableMonths[0]);
                  setPage(1);
                }}
              >
                {t("Specific Month")}
              </button>
              <button
                type="button"
                className={`carts-preset-pill ${datePreset === "range" ? "carts-preset-pill--active" : ""}`}
                onClick={() => {
                  setDatePreset("range");
                  setPage(1);
                }}
              >
                {t("Date Range")}
              </button>
            </div>
          </div>

          {/* Pole wyboru konkretnego dnia */}
          {datePreset === "day" && (
            <div className="carts-filter-item">
              <span className="carts-filter-label">{t("Select Day")}:</span>
              <input
                type="date"
                className="carts-date-input"
                value={customDay}
                onChange={(e) => {
                  setCustomDay(e.target.value);
                  setPage(1);
                }}
              />
            </div>
          )}

          {/* Pole wyboru konkretnego miesiąca */}
          {datePreset === "month" && (
            <div className="carts-filter-item">
              <span className="carts-filter-label">{t("Select Month")}:</span>
              {stats.availableMonths.length > 0 ? (
                <select
                  className="carts-select"
                  value={customMonth}
                  onChange={(e) => {
                    setCustomMonth(e.target.value);
                    setPage(1);
                  }}
                >
                  {stats.availableMonths.map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              ) : (
                <input
                  type="month"
                  className="carts-date-input"
                  value={customMonth}
                  onChange={(e) => {
                    setCustomMonth(e.target.value);
                    setPage(1);
                  }}
                />
              )}
            </div>
          )}

          {/* Pole wyboru zakresu dat (Od - Do) */}
          {datePreset === "range" && (
            <div className="carts-filter-group" style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div className="carts-filter-item">
                <span className="carts-filter-label">{t("From")}:</span>
                <input
                  type="date"
                  className="carts-date-input"
                  value={customRangeFrom}
                  onChange={(e) => {
                    setCustomRangeFrom(e.target.value);
                    setPage(1);
                  }}
                />
              </div>
              <div className="carts-filter-item">
                <span className="carts-filter-label">{t("To")}:</span>
                <input
                  type="date"
                  className="carts-date-input"
                  value={customRangeTo}
                  onChange={(e) => {
                    setCustomRangeTo(e.target.value);
                    setPage(1);
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Sekcja wykresów (Dashboard Charts) */}
      {(viewMode === "all" || viewMode === "dashboard") && (
        <div className="carts-charts-grid">
          {/* Wykres 1: Udział po producentach (Doughnut) */}
          <div className="cart-chart-card">
            <div className="cart-chart-header">
              <h3 className="cart-chart-title">
                <PieChart size={15} color="var(--adm-primary)" />
                {t("Share by Supplier")}
              </h3>
            </div>
            <div className="cart-chart-wrap">
              <canvas ref={canvasRefPie} />
            </div>
          </div>

          {/* Wykres 2: Top systemy (Bar) */}
          <div className="cart-chart-card">
            <div className="cart-chart-header">
              <h3 className="cart-chart-title">
                <BarChart3 size={15} color="var(--adm-primary)" />
                {t("Top Systems")}
              </h3>
            </div>
            <div className="cart-chart-wrap">
              <canvas ref={canvasRefSys} />
            </div>
          </div>

          {/* Wykres 3: Konfiguracje w czasie (Stacked Bar) */}
          <div className="cart-chart-card">
            <div className="cart-chart-header">
              <h3 className="cart-chart-title">
                <Clock size={15} color="var(--adm-primary)" />
                {t("Configurations over Time")}
              </h3>
            </div>
            <div className="cart-chart-wrap-lg">
              <canvas ref={canvasRefTime} />
            </div>
          </div>

          {/* Wykres 4: Wartość netto po producentach (Horizontal Bar) */}
          <div className="cart-chart-card">
            <div className="cart-chart-header">
              <h3 className="cart-chart-title">
                <TrendingUp size={15} color="var(--adm-primary)" />
                {t("Net Value by Supplier (€)")}
              </h3>
            </div>
            <div className="cart-chart-wrap-lg">
              <canvas ref={canvasRefVal} />
            </div>
          </div>
        </div>
      )}

      {/* Sekcja: Lieferanten — Detail (Tabela dostawców) */}
      {(viewMode === "all" || viewMode === "dashboard") && stats.bySupplier.length > 0 && (
        <div>
          <div className="carts-section-header">
            {t("Suppliers — Detail")}
            <span>({stats.bySupplier.length} {t("Supplier")})</span>
          </div>

          <div className="admin-card" style={{ padding: 0, overflow: "hidden" }}>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>{t("Supplier")}</th>
                  <th>{t("Count")}</th>
                  <th style={{ width: "24%" }}>{t("Share")}</th>
                  <th>{t("Total Net")}</th>
                  <th>{t("Avg Net (€)")}</th>
                  <th>{t("Ordered")}</th>
                </tr>
              </thead>
              <tbody>
                {stats.bySupplier.map((sup) => {
                  const sColor = getSupplierColor(sup.name);
                  const pct = (sup.count / maxSupplierCount) * 100;
                  return (
                    <tr key={sup.name}>
                      <td style={{ fontWeight: 700 }}>
                        <span className="supplier-dot" style={{ backgroundColor: sColor }} />
                        {sup.name}
                      </td>
                      <td style={{ fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>
                        {sup.count}
                      </td>
                      <td>
                        <div className="cart-progress-bar-wrap">
                          <div
                            className="cart-progress-bar"
                            style={{ width: `${pct}%`, backgroundColor: sColor }}
                          />
                        </div>
                      </td>
                      <td style={{ fontVariantNumeric: "tabular-nums", fontWeight: 700, color: "var(--adm-navy)" }}>
                        {formatCurrency(sup.netto)}
                      </td>
                      <td style={{ fontVariantNumeric: "tabular-nums", color: "var(--adm-text-muted)" }}>
                        {formatCurrency(sup.avgNetto)}
                      </td>
                      <td>
                        <span className="tag tag-b" style={{ background: "#E6F4EA", color: "#137333", padding: "2px 8px", borderRadius: 4, fontWeight: 700, fontSize: 11 }}>
                          {sup.orderedCount} ({formatCurrency(sup.orderedNetto)})
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Sekcja: Pojedyncze konfiguracje / Koszyki (Tabela) */}
      {(viewMode === "all" || viewMode === "table") && (
        <div>
          <div className="carts-section-header">
            {t("Individual Configurations")}
            <span>({totalFiltered} {t("Entries")})</span>
          </div>

          <div className="admin-card" style={{ padding: 0, overflow: "hidden" }}>
            {loading && carts.length === 0 ? (
              <div className="admin-empty">{t("Ładowanie...")}</div>
            ) : carts.length === 0 ? (
              <div className="admin-empty">{t("No carts found matching your criteria.")}</div>
            ) : (
              <>
                <table className="admin-table carts-table">
                  <thead>
                    <tr>
                      <th className="sortable" onClick={() => handleSort("id")}>
                        {t("Order-ID")} {sortKey === "id" && (sortDir === "asc" ? "↑" : "↓")}
                      </th>
                      <th className="sortable" onClick={() => handleSort("supplier")}>
                        {t("Supplier")} {sortKey === "supplier" && (sortDir === "asc" ? "↑" : "↓")}
                      </th>
                      <th className="sortable" onClick={() => handleSort("model")}>
                        {t("System")} {sortKey === "model" && (sortDir === "asc" ? "↑" : "↓")}
                      </th>
                      <th className="sortable" onClick={() => handleSort("doors")}>
                        {t("Pos.")} {sortKey === "doors" && (sortDir === "asc" ? "↑" : "↓")}
                      </th>
                      <th className="sortable" onClick={() => handleSort("keys")}>
                        {t("Keys")} {sortKey === "keys" && (sortDir === "asc" ? "↑" : "↓")}
                      </th>
                      <th className="sortable" onClick={() => handleSort("netto")}>
                        {t("Net (€)")} {sortKey === "netto" && (sortDir === "asc" ? "↑" : "↓")}
                      </th>
                      <th className="sortable" onClick={() => handleSort("status")}>
                        {t("Order type")} {sortKey === "status" && (sortDir === "asc" ? "↑" : "↓")}
                      </th>
                      <th className="sortable" onClick={() => handleSort("date")}>
                        {t("Date")} {sortKey === "date" && (sortDir === "asc" ? "↑" : "↓")}
                      </th>
                      <th style={{ textAlign: "right" }}>{t("Actions")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedCarts.map((cart) => {
                      const sColor = getSupplierColor(cart.supllier);
                      return (
                        <tr key={cart._id}>
                          <td>
                            {cart.orderid ? (
                              <span className="cart-row-orderid">{cart.orderid}</span>
                            ) : (
                              <span className="admin-code" title={cart._id}>
                                {cart._id.slice(0, 8)}...
                              </span>
                            )}
                          </td>
                          <td>
                            <span style={{ fontWeight: 600 }}>
                              <span className="supplier-dot" style={{ backgroundColor: sColor }} />
                              {cart.supllier}
                            </span>
                          </td>
                          <td>
                            <span style={{ color: "var(--adm-text)" }}>{cart.model}</span>
                          </td>
                          <td>
                            <span title={`${cart.totalDoorsAmount} Zylinder`}>
                              {cart.doorsCount}
                            </span>
                          </td>
                          <td>
                            <span>{cart.totalKeysAmount}</span>
                          </td>
                          <td>
                            <div className="cart-row-price">
                              {formatCurrency(cart.netto)}
                            </div>
                            <div className="cart-row-price-sub">
                              {formatCurrency(cart.brutto)} brutto
                            </div>
                          </td>
                          <td>{renderStatusBadge(cart.status)}</td>
                          <td>
                            <span style={{ fontSize: 12, color: "var(--adm-text-muted)" }}>
                              {formatDate(cart.createdAt)}
                            </span>
                          </td>
                          <td>
                            <div className="cart-actions" style={{ justifyContent: "flex-end" }}>
                              <button
                                type="button"
                                className="admin-btn admin-btn--ghost admin-btn--sm"
                                onClick={() => openCartDetail(cart._id)}
                                title={t("Details")}
                              >
                                <Eye size={14} /> {t("Details")}
                              </button>
                              <button
                                type="button"
                                className="admin-btn admin-btn--danger-ghost admin-btn--sm"
                                onClick={() => handleDeleteCart(cart._id)}
                                title={t("Delete")}
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {/* Paginacja */}
                {totalPages > 1 && (
                  <div className="carts-pagination">
                    <span>
                      {t("Pozycji:")} {totalFiltered} &middot; {t("Strona")} {page} {t("z")} {totalPages}
                    </span>
                    <div className="carts-pagination-controls">
                      <button
                        type="button"
                        className="admin-btn admin-btn--ghost admin-btn--sm"
                        disabled={page <= 1}
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                      >
                        <ChevronLeft size={16} />
                      </button>
                      <button
                        type="button"
                        className="admin-btn admin-btn--ghost admin-btn--sm"
                        disabled={page >= totalPages}
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      >
                        <ChevronRight size={16} />
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Modal ze szczegółami koszyka */}
      {selectedCartId && (
        <div className="cart-modal-backdrop" onClick={closeCartDetail}>
          <div className="cart-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cart-modal-header">
              <div className="cart-modal-title-wrap">
                <h3 className="cart-modal-title">
                  {cartDetail?.orderid || t("Cart details")}
                </h3>
                {cartDetail && renderStatusBadge(cartDetail.status)}
              </div>
              <button
                type="button"
                className="admin-btn admin-btn--ghost"
                onClick={closeCartDetail}
                title={t("Close")}
              >
                <X size={18} />
              </button>
            </div>

            <div className="cart-modal-body">
              {loadingDetail || !cartDetail ? (
                <div className="admin-empty">{t("Ładowanie...")}</div>
              ) : (
                <>
                  {/* Podsumowanie systemowe i cenowe */}
                  <div className="cart-detail-info-grid">
                    <div className="cart-detail-box">
                      <div className="cart-modal-section-title">
                        <Layers size={14} />
                        {t("System / Supplier")}
                      </div>
                      <p>
                        <strong>{t("Lieferant")}:</strong> {cartDetail.supllier}
                      </p>
                      <p>
                        <strong>{t("Modell")}:</strong> {cartDetail.model}
                      </p>
                      <p>
                        <strong>{t("Erstellt am")}:</strong> {formatDate(cartDetail.createdAt)}
                      </p>
                      {cartDetail.orderedAt && (
                        <p>
                          <strong>{t("Bestellt am")}:</strong> {formatDate(cartDetail.orderedAt)}
                        </p>
                      )}
                      {cartDetail.inquiryAt && (
                        <p>
                          <strong>{t("Anfrage am")}:</strong> {formatDate(cartDetail.inquiryAt)}
                        </p>
                      )}

                      {/* Wartość koszyka */}
                      <div className="cart-price-highlight">
                        <div>
                          <span style={{ fontSize: 11, textTransform: "uppercase", color: "var(--adm-text-muted)" }}>
                            {t("Total Net")}:
                          </span>
                          <div className="cart-price-netto">
                            {formatCurrency(cartDetail.netto)}
                          </div>
                        </div>
                        <div className="cart-price-brutto">
                          {formatCurrency(cartDetail.brutto)} brutto
                        </div>
                      </div>
                    </div>

                    {/* Dane klienta */}
                    <div className="cart-detail-box">
                      <div className="cart-modal-section-title">
                        <User size={14} />
                        {t("Customer")}
                      </div>
                      {cartDetail.customer?.name || cartDetail.customer?.email ? (
                        <>
                          <p>
                            <strong>{t("Name")}:</strong> {cartDetail.customer.name || "—"}
                          </p>
                          {cartDetail.customer.company && (
                            <p>
                              <strong>{t("Firma")}:</strong> {cartDetail.customer.company}
                            </p>
                          )}
                          <p>
                            <strong>{t("Email")}:</strong> {cartDetail.customer.email || "—"}
                          </p>
                          <p>
                            <strong>{t("Telefon")}:</strong> {cartDetail.customer.phone || "—"}
                          </p>
                          <p>
                            <strong>{t("Adresse")}:</strong>{" "}
                            {[
                              cartDetail.customer.streetAndHouse,
                              cartDetail.customer.zipCode,
                              cartDetail.customer.city,
                              cartDetail.customer.land,
                            ]
                              .filter(Boolean)
                              .join(", ") || "—"}
                          </p>
                        </>
                      ) : (
                        <p className="admin-text-faint">
                          {t("Keine Kundendaten (Warenkorb noch nicht abgesendet)")}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Uwagi klienta */}
                  {cartDetail.comment && (
                    <div>
                      <div className="cart-modal-section-title">
                        {t("Customer comments")}
                      </div>
                      <div className="cart-comment-box">{cartDetail.comment}</div>
                    </div>
                  )}

                  {/* Tabela drzwi */}
                  <div>
                    <div className="cart-modal-section-title">
                      {t("Doors")} ({cartDetail.doors?.length || 0})
                    </div>
                    {cartDetail.doors && cartDetail.doors.length > 0 ? (
                      <table className="admin-table">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>{t("Bezeichnung")}</th>
                            <th>{t("Typ")}</th>
                            <th>{t("Menge")}</th>
                            <th>{t("Außenmaß")}</th>
                            <th>{t("Innenmaß")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {cartDetail.doors.map((d, idx) => (
                            <tr key={d.id || idx}>
                              <td>{idx + 1}</td>
                              <td style={{ fontWeight: 600 }}>{d.doorName}</td>
                              <td><span className="admin-code">{d.lockType}</span></td>
                              <td>{d.lockAmount}</td>
                              <td>{d.lockOutside} mm</td>
                              <td>{d.lockInside} mm</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <p className="admin-text-faint">{t("Brak danych o drzwiach.")}</p>
                    )}
                  </div>

                  {/* Tabela kluczy */}
                  <div>
                    <div className="cart-modal-section-title">
                      {t("Keys")} ({cartDetail.keys?.length || 0})
                    </div>
                    {cartDetail.keys && cartDetail.keys.length > 0 ? (
                      <table className="admin-table">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>{t("Bezeichnung")}</th>
                            <th>{t("Menge")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {cartDetail.keys.map((k, idx) => (
                            <tr key={k.id || idx}>
                              <td>{idx + 1}</td>
                              <td style={{ fontWeight: 600 }}>{k.keyName}</td>
                              <td>{k.amount} {t("Stk.")}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <p className="admin-text-faint">{t("Brak danych o kluczach.")}</p>
                    )}
                  </div>

                  {/* Macierz uprawnień */}
                  {cartDetail.opensDoor && cartDetail.opensDoor.length > 0 && cartDetail.doors && cartDetail.keys && (
                    <div>
                      <div className="cart-modal-section-title">
                        {t("Access permissions")}
                      </div>
                      <table className="admin-table cart-matrix-table">
                        <thead>
                          <tr>
                            <th>{t("Tür")} \ {t("Keys")}</th>
                            {cartDetail.keys.map((k) => (
                              <th key={k.id}>{k.keyName}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {cartDetail.doors.map((d) => (
                            <tr key={d.id}>
                              <td style={{ fontWeight: 600 }}>{d.doorName}</td>
                              {cartDetail.keys?.map((k) => {
                                const allowed = cartDetail.opensDoor?.some(
                                  (p) => p.idDoor === d.id && p.idKey === k.id && (p.allow !== false)
                                );
                                return (
                                  <td key={k.id}>
                                    {allowed ? (
                                      <span className="cart-matrix-check">✓</span>
                                    ) : (
                                      <span style={{ color: "var(--adm-border)" }}>—</span>
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Stopka modalu ze zmianą statusu i akcjami */}
            <div className="cart-modal-footer">
              <div className="cart-status-changer">
                <label>{t("Status")}:</label>
                <select
                  className="carts-select"
                  value={cartDetail?.status || "abandoned"}
                  onChange={(e) => {
                    if (cartDetail) {
                      handleStatusChange(cartDetail._id, e.target.value as any);
                    }
                  }}
                >
                  <option value="abandoned">{t("Abandoned")}</option>
                  <option value="ordered">{t("Ordered")}</option>
                  <option value="inquiry">{t("Inquiries")}</option>
                </select>
              </div>

              <div style={{ display: "flex", gap: 8 }}>
                <button
                  type="button"
                  className="admin-btn admin-btn--danger-ghost"
                  onClick={() => {
                    if (cartDetail) handleDeleteCart(cartDetail._id);
                  }}
                >
                  <Trash2 size={14} />
                  {t("Delete")}
                </button>
                <button
                  type="button"
                  className="admin-btn admin-btn--secondary"
                  onClick={closeCartDetail}
                >
                  {t("Close")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Carts;
